interface PageLayoutProps {
  children: React.ReactNode;
  hero?: {
    title: string;
    subtitle: string;
    backgroundImage?: string;
    showSearch?: boolean;
    onSearch?: (query: string) => void;
  };
  error?: string;
  loading?: boolean;
  emptyState?: {
    title: string;
    description: string;
    action?: React.ReactNode;
  };
}

export function PageLayout({ 
  children, 
  hero, 
  error, 
  loading, 
  emptyState 
}: PageLayoutProps) {
  return (
    <div className="min-h-screen">
      {hero && (
        <div 
          className="relative text-white overflow-hidden min-h-[70vh] flex items-center bg-gradient-to-br from-blue-600 to-blue-800"
          style={hero.backgroundImage ? {
            backgroundImage: `url(${hero.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-blue-900/40" />
          
          {/* Content */}
          <div className="relative container mx-auto px-4 py-16 md:py-24 z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {hero.title}
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto leading-relaxed">
                {hero.subtitle}
              </p>

              {hero.showSearch && (
                <div className="max-w-2xl mx-auto">
                  <div className="flex flex-col sm:flex-row gap-4 bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 shadow-2xl">
                    <input
                      type="text"
                      placeholder="Buscar ubicación..."
                      className="flex-1 px-4 py-3 rounded-lg bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/50"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && hero.onSearch) {
                          hero.onSearch((e.target as HTMLInputElement).value);
                        }
                      }}
                    />
                    <button 
                      className="px-8 py-3 bg-white text-blue-900 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        const input = document.querySelector('input[placeholder="Buscar ubicación..."]') as HTMLInputElement;
                        if (input && hero.onSearch) {
                          hero.onSearch(input.value);
                        }
                      }}
                    >
                      🔍 Buscar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="text-red-700">❌ {error}</div>
          </div>
        )}

        {!loading && !error && emptyState && (
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {emptyState.title}
            </h3>
            <p className="text-gray-600 mb-6">
              {emptyState.description}
            </p>
            {emptyState.action}
          </div>
        )}

        {!error && !emptyState && children}
      </div>
    </div>
  );
}